/**
 * 提问范围（问答框底部三个胶囊）的配置服务。
 *
 * 「谁可见、可选什么、默认选什么」由服务端最终裁定；前端只渲染获授权选项，
 * 并在提交前用该列表清理过期选择，不能替代服务端权限校验。
 */
import request from "../../utils/request";

/** General Docs 的可选项：用户被授权的文档分类 */
export interface AskScopeCategory {
  id: string;
  name: string;
  code: string;
}

/** Study Source 胶囊的 Study 级可选项，并携带该 Study 下已授权的具体 Source */
export interface AskScopeStudy {
  id: string;
  code: string;
  name: string;
  studySourceIds: string[];
}

export interface AskScope {
  region: string;
  isAdmin: boolean;
  generalDocs: {
    visible: boolean;
    categories: AskScopeCategory[];
    /** 区域锁定分类 + 后台配置为默认勾选且当前用户有权访问的分类 */
    defaultCategoryIds: string[];
  };
  studySource: {
    visible: boolean;
    studies: AskScopeStudy[];
  };
  personalDocs: {
    visible: boolean;
  };
}

export type AskScopePill = "general" | "myStudies" | "personal";

interface AskScopeWire {
  Region?: string;
  IsAdmin?: boolean;
  GeneralDocs?: {
    Visible?: boolean;
    Categories?: { ID?: string; Name?: string; Code?: string }[];
    DefaultCategoryIds?: string[];
  };
  StudySource?: {
    Visible?: boolean;
    Studies?: {
      ID?: string;
      Code?: string;
      Name?: string;
      StudySourceIds?: string[];
    }[];
    /** 兼容过渡期 API：按 StudyId 将 Source 明细聚合成 Study。 */
    Sources?: {
      ID?: string;
      StudyId?: string;
      StudyCode?: string;
      StudyName?: string;
    }[];
  };
  PersonalDocs?: { Visible?: boolean };
}

/** 拉取失败时的兜底：三个胶囊全部隐藏，宁可少给范围也不越权展示 */
export const EMPTY_ASK_SCOPE: AskScope = {
  region: "Mainland",
  isAdmin: false,
  generalDocs: { visible: false, categories: [], defaultCategoryIds: [] },
  studySource: { visible: false, studies: [] },
  personalDocs: { visible: false },
};

function normalizeStudies(studySource: AskScopeWire["StudySource"]): AskScopeStudy[] {
  const studies = (studySource?.Studies ?? []).map((study) => ({
    id: String(study.ID ?? ""),
    code: study.Code ?? "",
    name: study.Name ?? "",
    studySourceIds: [...new Set((study.StudySourceIds ?? []).map(String).filter(Boolean))],
  }));
  if (studies.length > 0) return studies;

  const grouped = new Map<string, AskScopeStudy>();
  (studySource?.Sources ?? []).forEach((source) => {
    const sourceId = String(source.ID ?? "").trim();
    if (!sourceId) return;

    const code = source.StudyCode ?? "";
    const name = source.StudyName ?? "";
    const studyId = String(source.StudyId ?? "").trim() || `legacy:${code}:${name}`;
    const existing = grouped.get(studyId);
    if (existing) {
      if (!existing.studySourceIds.includes(sourceId)) existing.studySourceIds.push(sourceId);
      return;
    }
    grouped.set(studyId, { id: studyId, code, name, studySourceIds: [sourceId] });
  });
  return Array.from(grouped.values());
}

/**
 * 本次提问实际生效的分类 ID。
 * 胶囊关闭 → 空数组（明确表示不检索 General 来源）；
 * 胶囊开启且选择 null → 空数组（下拉里全不选的语义）；
 * 胶囊开启且选择空数组 → 全部授权分类（下拉里「全部类型」的语义）；
 * 有细选 → 仍以服务端返回的 allIds 为白名单过滤，旧状态不能扩大权限。
 */
export function resolveEffectiveCategoryIds(
  generalDocsEnabled: boolean,
  selectedIds: string[] | null,
  allIds: string[],
): string[] {
  if (!generalDocsEnabled) return [];
  if (selectedIds === null) return [];
  const authorizedIds = new Set(allIds);
  const candidates = selectedIds.length > 0 ? selectedIds : allIds;
  return [...new Set(candidates.filter((id) => authorizedIds.has(id)))];
}

/**
 * 将 Study 级 checkbox 展开成本次提问实际生效的 SourcePathStudy.ID。
 * 胶囊关闭或选择 null → 空数组；胶囊开启且选择空数组 → 当前用户全部获授权 Study。
 */
export function resolveEffectiveStudySourceIds(
  studySourceEnabled: boolean,
  selectedStudyIds: string[] | null,
  studies: AskScopeStudy[],
): string[] {
  if (!studySourceEnabled) return [];
  if (selectedStudyIds === null) return [];

  const studyById = new Map(studies.map((study) => [study.id.trim().toLowerCase(), study]));
  const candidates =
    selectedStudyIds.length > 0 ? selectedStudyIds : studies.map((study) => study.id);
  const sourceIds = candidates.flatMap(
    (studyId) => studyById.get(studyId.trim().toLowerCase())?.studySourceIds ?? [],
  );
  return [...new Set(sourceIds)];
}

/**
 * General Docs 的初始勾选由后端给出：区域锁定分类，加上后台配置为默认勾选的
 * 已授权分类。再次按授权列表过滤，避免历史状态让无权分类被选中。
 */
export function resolveDefaultCategoryIds(generalDocs: AskScope["generalDocs"]): string[] {
  const authorizedIds = new Set(generalDocs.categories.map((c) => c.id));
  return generalDocs.defaultCategoryIds.filter((id) => authorizedIds.has(id));
}

/** Ask 提问页默认启用共享范围；Personal Docs 必须由用户显式勾选。 */
export function resolveDefaultScopePills(scope: AskScope): AskScopePill[] {
  const pills: AskScopePill[] = [];
  if (scope.generalDocs.visible) pills.push("general");
  if (scope.studySource.visible) pills.push("myStudies");
  return pills;
}

export function getAskScope(): Promise<AskScope> {
  return request.get<AskScopeWire>("/project/askscope/mine").then((res) => {
    const wire = res.data ?? {};
    return {
      region: wire.Region ?? "Mainland",
      isAdmin: !!wire.IsAdmin,
      generalDocs: {
        visible: !!wire.GeneralDocs?.Visible,
        categories: (wire.GeneralDocs?.Categories ?? []).map((c) => ({
          id: String(c.ID ?? ""),
          name: c.Name ?? "",
          code: c.Code ?? "",
        })),
        defaultCategoryIds: (wire.GeneralDocs?.DefaultCategoryIds ?? []).map(String),
      },
      studySource: {
        visible: !!wire.StudySource?.Visible,
        studies: normalizeStudies(wire.StudySource),
      },
      personalDocs: { visible: !!wire.PersonalDocs?.Visible },
    };
  });
}
